import { useId, useLayoutEffect, useMemo, useRef } from "react";
import { atom, useAtomValue, useSetAtom } from "jotai";
import type { DefinityTourDef } from "./types";

const toursAtom = atom<Map<string, DefinityTourDef[]>>(new Map());

/** Exported for LinkWithModal SCOPED_ATOMS (modal isolation). */
export { toursAtom };

/** Register a component's tours while it is mounted. Pass a stable (module-const)
 * array; gate by passing [] when the tours should not be offered. */
export const useRegisterTours = (tours: DefinityTourDef[]) => {
  const id = useId();
  const setTours = useSetAtom(toursAtom);
  const toursRef = useRef(tours);
  toursRef.current = tours;
  const signature = tours.map((t) => t.id).join("\x00");

  // biome-ignore lint/correctness/useExhaustiveDependencies: setTours stable; tours via ref + signature
  useLayoutEffect(() => {
    setTours((prev) => new Map(prev).set(id, toursRef.current));
    return () =>
      setTours((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
  }, [id, signature, setTours]);
};

/** Flattened, id-deduped list of all currently-registered tours. */
export const useRegisteredTours = (): DefinityTourDef[] => {
  const map = useAtomValue(toursAtom);
  return useMemo(() => {
    const byId = new Map<string, DefinityTourDef>();
    for (const t of [...map.values()].flat()) {
      const existing = byId.get(t.id);
      if (existing && existing !== t) {
        console.warn(
          `[tours] duplicate tour id "${t.id}" registered; keeping first`
        );
        continue;
      }
      byId.set(t.id, t);
    }
    return [...byId.values()];
  }, [map]);
};
